import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/apiClient";

/**
 * THE BOOKS, READ. Every figure here is derived from what the clinic already
 * records — invoices, claims and voids — and nothing in this lane writes to
 * them. The accountant's home says so on its own header: "the read layer with
 * export rights".
 *
 * MONEY IS COUNTED WHEN IT IS COLLECTED, not when it is billed. An invoice
 * raised on 30 August and paid in September is September's revenue, which is
 * why "outstanding" is a balance at a moment rather than a sum over a period:
 * a period-scoped one would drop last quarter's unpaid claim off the books the
 * day the quarter turned, and that claim is the whole reason to look.
 */

const unwrap = <T>(body: unknown): T =>
  ((body as { data?: T })?.data ?? body) as T;

export interface AccountsSummary {
  period: string;
  collectedMinor: number;
  vatMinor: number;
  refundedMinor: number;
  outstandingMinor: number;
  patientsOweMinor: number;
  insurersOweMinor: number;
  billed: number;
  branches: string[];
}

export interface Receivable {
  kind: "patient" | "insurer";
  id: string | null;
  name: string;
  owedMinor: number;
  current: number;
  d30: number;
  d60: number;
  d90: number;
  oldestDays: number;
  items: number;
}

export function useAccountsSummary(period: string) {
  return useQuery({
    queryKey: ["accounts", "summary", period],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<AccountsSummary | null> => {
      const res = await apiClient.get("/accounts/summary", {
        params: { period },
      });
      return unwrap<AccountsSummary>(res.data);
    },
  });
}

export function useReceivables() {
  return useQuery({
    queryKey: ["accounts", "receivables"],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<Receivable[]> => {
      const res = await apiClient.get("/accounts/receivables");
      const rows = unwrap<Receivable[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

/* ── the period picker every board in this lane shares ────────────────────── */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** `2026-08` → "August" — the boards name a month in full when it is a label. */
export function monthName(period: string): string {
  const m = Number(period.split("-")[1]);
  return m >= 1 && m <= 12 ? MONTHS_FULL[m - 1] : period;
}

/** `2026-08` → "Aug 2026". */
export function periodLabel(period: string): string {
  const [y, m] = period.split("-").map(Number);
  return m >= 1 && m <= 12 ? `${MONTHS[m - 1]} ${y}` : period;
}

export function thisPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** The three the boards draw: the month, and the two before it. */
export function recentPeriods(count = 3): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = count - 1; i >= 0; i -= 1) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

/** "EGP 191,692" — the books round to the pound; the ledger keeps the piastres. */
export function money(minor: number, currency = "EGP", exact = false): string {
  return `${currency} ${amount(minor, exact)}`;
}

/** Without the currency, for a stat card that already says what it is. */
export function amount(minor: number, exact = false): string {
  return (minor / 100).toLocaleString("en-US", exact
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { maximumFractionDigits: 0 });
}

/** True when any figure carries piastres — a set of totals that are meant to
 *  add up is then printed to the piastre, because rounding each one to the
 *  pound is exactly how 49,429 − 960 + 120 comes out as 48,589. */
export function hasPiastres(...minor: number[]): boolean {
  return minor.some((v) => Math.round(v) % 100 !== 0);
}

/* ── expenses (4, 4b, 4c, 5, 5b, 5c) ──────────────────────────────────────── */

export type ExpenseCategory =
  | "people"
  | "rent"
  | "consumables"
  | "utilities"
  | "subscriptions"
  | "equipment"
  | "marketing"
  | "other";

export const CATEGORY_OPTIONS: Array<{
  value: ExpenseCategory;
  label: string;
  hint?: string;
}> = [
  { value: "rent", label: "Rent" },
  {
    value: "utilities",
    label: "Utilities",
    hint: "electricity, water, internet",
  },
  { value: "subscriptions", label: "Subscriptions" },
  {
    value: "equipment",
    label: "Equipment",
    hint: "bought outright, not consumed",
  },
  { value: "marketing", label: "Marketing" },
  { value: "people", label: "People we pay", hint: "outside the payout run" },
  {
    value: "consumables",
    label: "Consumables",
    hint: "normally posted from stock",
  },
  { value: "other", label: "Other" },
];

export interface CategoryRow {
  category: ExpenseCategory;
  label: string;
  sub: string;
  totalMinor: number;
  share: number;
  count: number;
  undocumentedMinor: number;
}

export interface ExpenseMonth {
  period: string;
  totalMinor: number;
  previousMinor: number;
  undocumentedMinor: number;
  undocumentedCount: number;
  categories: CategoryRow[];
  postedByRecurring: number;
  branches: string[];
}

export interface ExpenseLine {
  id: string;
  category: ExpenseCategory;
  description: string;
  amountMinor: number;
  spentOn: string;
  paidWith: string | null;
  supplierName: string | null;
  documentId: string | null;
  documentName: string | null;
  /** The VAT inside the amount, reclaimable. */
  vatMinor?: number | null;
  sourceKind: "manual" | "recurring" | "stock" | "payroll";
  note: string | null;
}

export interface RecurringLine {
  id: string;
  label: string;
  category: ExpenseCategory;
  /** Null means VARIABLE: it reminds on its day and posts nothing. */
  amountMinor: number | null;
  dayOfMonth: number;
  paidWith: string | null;
  supplierName: string | null;
  isActive: boolean;
  note: string | null;
  state: "posted" | "posted_undocumented" | "reminder" | "not_yet" | "stopped";
  postedExpenseId: string | null;
}

export function useExpenseMonth(period: string) {
  return useQuery({
    queryKey: ["expenses", "month", period],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<ExpenseMonth | null> => {
      const res = await apiClient.get("/expenses", { params: { period } });
      return unwrap<ExpenseMonth>(res.data);
    },
  });
}

export function useExpenseLines(
  period: string,
  category?: ExpenseCategory | null,
) {
  return useQuery({
    queryKey: ["expenses", "lines", period, category ?? "all"],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<ExpenseLine[]> => {
      const res = await apiClient.get("/expenses/lines", {
        params: { period, ...(category ? { category } : {}) },
      });
      const rows = unwrap<ExpenseLine[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

export function useRecurring(period: string) {
  return useQuery({
    queryKey: ["expenses", "recurring", period],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<RecurringLine[]> => {
      const res = await apiClient.get("/expenses/recurring", {
        params: { period },
      });
      const rows = unwrap<RecurringLine[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

/** File an invoice — a photo or a PDF — and get its id back to attach. */
export async function uploadExpenseDocument(
  file: File,
): Promise<{ id: string; name: string; mimeType: string; sizeBytes: number }> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiClient.post("/expenses/documents", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return unwrap(res.data);
}

const OPENABLE = /^(image\/(jpeg|png|webp|heic|heif)|application\/pdf)$/i;

/**
 * Open a filed invoice. The endpoint wants the bearer token, so it is fetched,
 * not linked. A blob: URL runs in OUR origin, so only a photo or a PDF is ever
 * opened (re-wrapped under that exact type); anything else is downloaded as
 * opaque bytes and never rendered.
 */
export async function openExpenseDocument(id: string): Promise<void> {
  const res = await apiClient.get(`/expenses/documents/${id}`, {
    responseType: "blob",
  });
  const data = res.data as Blob;
  const type = (data.type || "").split(";")[0].trim().toLowerCase();
  const safe = OPENABLE.test(type);
  const url = URL.createObjectURL(
    new Blob([data], { type: safe ? type : "application/octet-stream" }),
  );
  if (safe) {
    window.open(url, "_blank", "noopener");
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = "invoice";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export interface RecurringHistory {
  months: Array<{
    period: string;
    expenseId: string;
    amountMinor: number;
    spentOn: string;
    documentId: string | null;
    documentName: string | null;
  }>;
  documents: number;
}

export function useRecurringHistory(id: string | null) {
  return useQuery({
    queryKey: ["expenses", "recurring-history", id],
    enabled: !!id,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<RecurringHistory | null> => {
      const res = await apiClient.get(`/expenses/recurring/${id}/history`);
      return unwrap<RecurringHistory>(res.data);
    },
  });
}

export function useExpenseActions() {
  const qc = useQueryClient();
  const done = {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["expenses"] }),
  };
  return {
    record: useMutation({
      mutationFn: async (
        input: Partial<ExpenseLine> & {
          amountMinor: number;
          description: string;
        },
      ) => {
        const res = await apiClient.post("/expenses", input);
        return unwrap<ExpenseLine>(res.data);
      },
      ...done,
    }),
    attach: useMutation({
      mutationFn: async ({
        id,
        documentName,
        documentId,
        description,
        amountMinor,
      }: {
        id: string;
        documentName: string;
        documentId?: string;
        description: string;
        amountMinor: number;
      }) => {
        await apiClient.patch(`/expenses/${id}`, {
          description,
          amountMinor,
          documentName,
          ...(documentId ? { documentId } : {}),
        });
      },
      ...done,
    }),
    addRecurring: useMutation({
      mutationFn: async (input: Partial<RecurringLine> & { label: string }) => {
        const res = await apiClient.post("/expenses/recurring", input);
        return unwrap<RecurringLine>(res.data);
      },
      ...done,
    }),
    stopRecurring: useMutation({
      mutationFn: async ({
        id,
        label,
        isActive,
      }: {
        id: string;
        label: string;
        isActive: boolean;
      }) => {
        await apiClient.patch(`/expenses/recurring/${id}`, { label, isActive });
      },
      ...done,
    }),
  };
}

/* ── people we pay (6, 6b, 7, 7b, 7c, 7d, 8) ──────────────────────────────── */

export type DealKind =
  | "percent"
  | "salary"
  | "per_visit"
  | "hybrid"
  | "room_rent"
  | "commission";

export const DEAL_LABEL: Record<DealKind, string> = {
  percent: "Percentage of collected",
  salary: "Fixed salary",
  per_visit: "Per visit",
  hybrid: "Salary plus a share",
  room_rent: "Room rent — they pay the clinic",
  commission: "Commission",
};

export interface RunLine {
  id: string | null;
  personId: string;
  personName: string;
  lane: "doctors" | "staff";
  dealKind: DealKind;
  dealSummary: string | null;
  baseMinor: number;
  grossMinor: number;
  deductionsMinor: number;
  additionsMinor: number;
  netMinor: number;
  status: string;
  /** The clinic is owed, not owing — the room-rent lane. */
  reverse: boolean;
  settledAt: string | null;
  note: string | null;
  paidMethod: string | null;
  paidReference: string | null;
  /** The role the staff lane groups people by. */
  roleName: string | null;
  slip: SlipBreakdown | null;
}

/** What makes up a salaried net — overtime, absence, advance, social insurance. */
export interface SlipBreakdown {
  basicMinor: number;
  overtimeHours: number;
  overtimeMinor: number;
  absenceDays: number;
  absenceMinor: number;
  advanceMinor: number;
  socialInsuranceMinor: number;
}

export interface SlipContext {
  roleName: string | null;
  /** Empty = all branches. */
  branches: string[];
  employedSince: string | null;
  absenceDates: string[];
  advanceBalanceMinor: number;
  advanceMonthlyMinor: number;
  advanceMonthsLeft: number | null;
  slip: SlipBreakdown | null;
}

export interface PayrollRun {
  id: string | null;
  period: string;
  status: "draft" | "sent" | "approved" | "settled";
  sentAt: string | null;
  sentBy: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  baseMinor: number;
  grossMinor: number;
  deductionsMinor: number;
  additionsMinor: number;
  netMinor: number;
  toCollectMinor: number;
  lines: RunLine[];
  /** Approved runs are locked — a correction goes on the next run. */
  locked: boolean;
  cutOff: string;
}

export const PAY_METHOD_LABEL: Record<string, string> = {
  bank_transfer: "bank transfer",
  cash: "cash",
  instapay: "InstaPay",
  cheque: "cheque",
};

export function usePayrollRun(period: string) {
  return useQuery({
    queryKey: ["payroll", period],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<PayrollRun | null> => {
      const res = await apiClient.get("/payroll", { params: { period } });
      return unwrap<PayrollRun>(res.data);
    },
  });
}

/** One person's statement — every line of money behind their share. */
export interface StatementRow {
  date: string;
  source: string;
  collectedMinor: number;
  sharePercent: number | null;
  earnedMinor: number;
  /** A claim that was paid carries its remittance reference. */
  remittance?: string | null;
  insured?: number;
  visits?: number;
}

export function usePayrollActions() {
  const qc = useQueryClient();
  const done = {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["payroll"] }),
  };
  return {
    send: useMutation({
      mutationFn: async (period: string) => {
        const res = await apiClient.post("/payroll/send", { period });
        return unwrap<PayrollRun>(res.data);
      },
      ...done,
    }),
    approve: useMutation({
      mutationFn: async (period: string) => {
        const res = await apiClient.post("/payroll/approve", { period });
        return unwrap<PayrollRun>(res.data);
      },
      ...done,
    }),
    settle: useMutation({
      mutationFn: async (input: {
        lineId: string;
        method: string;
        reference?: string;
      }) => {
        const { lineId, ...body } = input;
        const res = await apiClient.post(
          `/payroll/lines/${lineId}/settle`,
          body,
        );
        return unwrap<PayrollRun>(res.data);
      },
      ...done,
    }),
    setOvertime: useMutation({
      mutationFn: async (input: {
        personId: string;
        period: string;
        hours: number;
      }) => {
        await apiClient.put("/payroll/inputs/overtime", input);
      },
      ...done,
    }),
    giveAdvance: useMutation({
      mutationFn: async (input: {
        personId: string;
        amountMinor: number;
        monthlyMinor: number;
        note?: string;
      }) => {
        await apiClient.post("/payroll/advances", input);
      },
      ...done,
    }),
    sendPayslip: useMutation({
      mutationFn: async (input: { personId: string; period: string }) => {
        const res = await apiClient.post("/payroll/statement/send", input);
        return unwrap<{
          delivered: boolean;
          to: string | null;
          reason: string | null;
        }>(res.data);
      },
    }),
    addAdjustment: useMutation({
      mutationFn: async (input: {
        doctorId: string;
        period: string;
        kind: "deduction" | "addition";
        amountMinor: number;
        reason: string;
      }) => {
        await apiClient.post("/doctor-payouts/adjustments", input);
      },
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["payroll"] });
      },
    }),
  };
}

export interface StatementBill {
  number: string;
  collectedMinor: number;
  method: string | null;
  insured: boolean;
}

export interface StatementLine {
  date: string;
  source: string;
  visits: number;
  insured: number;
  collectedMinor: number;
  sharePercent: number | null;
  earnedMinor: number;
  /** The bills behind it — what a disputed line is answered with. */
  bills: StatementBill[];
  remittance: string | null;
}

export interface Statement {
  personId: string;
  personName: string;
  period: string;
  dealKind: DealKind;
  dealSummary: string | null;
  sharePercent: number | null;
  lines: StatementLine[];
  collectedMinor: number;
  /** What the lines add to now — can differ from gross on a frozen run. */
  linesEarnedMinor: number;
  grossMinor: number;
  deductionsMinor: number;
  additionsMinor: number;
  netMinor: number;
  adjustments: Array<{
    id: string;
    kind: string;
    label: string;
    amountMinor: number;
  }>;
  runStatus: string;
  context: SlipContext;
}

export function useStatement(personId: string | null, period: string) {
  return useQuery({
    queryKey: ["payroll", "statement", personId, period],
    enabled: !!personId,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<Statement | null> => {
      const res = await apiClient.get("/payroll/statement", {
        params: { personId, period },
      });
      return unwrap<Statement>(res.data);
    },
  });
}

/** "31 Aug" — a statement's dates are days, not instants. */
export function dayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/* ── payables (9, 9b) ─────────────────────────────────────────────────────── */

export interface Payable extends ExpenseLine {
  dueOn: string | null;
  paidOn: string | null;
  paidReference: string | null;
  overdueDays: number;
}

export interface Payables {
  openMinor: number;
  overdueMinor: number;
  creditsMinor: number;
  items: Payable[];
  /** Credit notes not yet netted — negative rows, from returns. */
  credits: Array<{
    id: string;
    supplierName: string;
    amountMinor: number;
    issuedOn: string;
    note: string | null;
  }>;
}

export interface ProviderProfile {
  name: string;
  inDirectory: boolean;
  providerId: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  orders: number;
  returns: number;
  owedMinor: number;
  overdueDays: number;
  creditsMinor: number;
}

export function useProviderProfile(name: string | null) {
  return useQuery({
    queryKey: ["expenses", "provider", name],
    enabled: !!name,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<ProviderProfile | null> => {
      const res = await apiClient.get("/expenses/payables/provider", {
        params: { name },
      });
      return unwrap<ProviderProfile>(res.data);
    },
  });
}

export function useAddCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      supplierName: string;
      amountMinor: number;
      note?: string;
    }) => {
      await apiClient.post("/expenses/credits", input);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["expenses"] }),
  });
}

export function usePayables() {
  return useQuery({
    queryKey: ["expenses", "payables"],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<Payables | null> => {
      const res = await apiClient.get("/expenses/payables");
      return unwrap<Payables>(res.data);
    },
  });
}

export function usePayPayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      paidReference,
    }: {
      id: string;
      paidReference: string;
    }) => {
      await apiClient.post(`/expenses/${id}/pay`, { paidReference });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["expenses"] }),
  });
}

/* ── the books (10, 11) ───────────────────────────────────────────────────── */

export interface TaxReport {
  period: string;
  /** The bill numbers issued in the period — what an inspector checks for holes. */
  billRange?: {
    first: string | null;
    last: string | null;
    issued: number;
    missing: number;
    deleted: number;
  };
  exemptBaseMinor: number;
  taxableBaseMinor: number;
  vatMinor: number;
  refundedVatMinor: number;
  refundedBaseMinor: number;
  refundCount: number;
  classes: Array<{
    key: "medical" | "products" | "cosmetic";
    sub: string;
    label: string;
    ratePercent: number;
    baseMinor: number;
    vatMinor: number;
    bills: number;
  }>;
  branches: string[];
}

export interface ProfitAndLoss {
  period: string;
  revenueMinor: number;
  refundsMinor: number;
  spendMinor: number;
  profitMinor: number;
  /** Null on a month with no revenue — a margin on nothing is not zero. */
  marginPercent: number | null;
  lines: Array<{ label: string; amountMinor: number; kind: "in" | "out" }>;
  previousProfitMinor: number;
  branches: string[];
}

export function useTaxReport(period: string) {
  return useQuery({
    queryKey: ["accounts", "tax", period],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<TaxReport | null> => {
      const res = await apiClient.get("/accounts/tax", { params: { period } });
      return unwrap<TaxReport>(res.data);
    },
  });
}

export interface BranchProfit {
  branchId: string | null;
  name: string;
  revenueMinor: number;
  refundsMinor: number;
  spendMinor: number;
  profitMinor: number;
}

export function useProfitByBranch(period: string, enabled: boolean) {
  return useQuery({
    queryKey: ["expenses", "pnl-branches", period],
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<BranchProfit[]> => {
      const res = await apiClient.get("/expenses/pnl/branches", {
        params: { period },
      });
      return unwrap<{ branches: BranchProfit[] }>(res.data)?.branches ?? [];
    },
  });
}

export function useProfitAndLoss(period: string) {
  return useQuery({
    queryKey: ["expenses", "pnl", period],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<ProfitAndLoss | null> => {
      const res = await apiClient.get("/expenses/pnl", { params: { period } });
      return unwrap<ProfitAndLoss>(res.data);
    },
  });
}

/* ── the export centre (13) ───────────────────────────────────────────────── */

export interface ExportLogEntry {
  id: string;
  tableKey: string;
  label: string;
  period: string | null;
  format: string;
  rows: number;
  pulledBy: string | null;
  createdAt: string;
}

export function useExportLog() {
  return useQuery({
    queryKey: ["accounts", "exports"],
    staleTime: 15_000,
    retry: false,
    queryFn: async (): Promise<ExportLogEntry[]> => {
      const res = await apiClient.get("/accounts/exports");
      const rows = unwrap<ExportLogEntry[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

/**
 * EVERY EXPORT IS LOGGED, which the centre promises on its own screen — so
 * the log entry is written as part of producing the file, not alongside it.
 */
export function useLogExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      tableKey: string;
      label: string;
      period?: string;
      format?: string;
      rows?: number;
    }) => {
      await apiClient.post("/accounts/exports", input);
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["accounts", "exports"] }),
  });
}
